"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

type BlogFilterNavigationContextValue = {
  isPending: boolean;
  startFilterNavigation: (action: () => void) => void;
  resultsTotal: number | null;
  syncResultsTotal: (total: number) => void;
};

const BlogFilterNavigationContext = createContext<BlogFilterNavigationContextValue | null>(
  null,
);

export function CcBlogFilterNavigationProvider({ children }: { children: ReactNode }) {
  const [resultsTotal, setResultsTotal] = useState<number | null>(null);

  /** Run navigation immediately — wrapping in useTransition aborted RSC streams on fast tab clicks. */
  const startFilterNavigation = useCallback((action: () => void) => {
    action();
  }, []);

  const isPending = false;

  const syncResultsTotal = useCallback((total: number) => {
    setResultsTotal(total);
  }, []);

  const value = useMemo(
    (): BlogFilterNavigationContextValue => ({
      isPending,
      startFilterNavigation,
      resultsTotal,
      syncResultsTotal,
    }),
    [isPending, startFilterNavigation, resultsTotal, syncResultsTotal],
  );

  return (
    <BlogFilterNavigationContext.Provider value={value}>
      {children}
    </BlogFilterNavigationContext.Provider>
  );
}

export function useBlogFilterNavigation(): BlogFilterNavigationContextValue {
  const context = useContext(BlogFilterNavigationContext);
  if (!context) {
    throw new Error("useBlogFilterNavigation must be used within CcBlogFilterNavigationProvider");
  }
  return context;
}
